import { Icon } from '@gftg/ui';
import { Link, useLocation } from 'react-router';
import { assetUrl } from '../lib/api';
import type { PublicEvent } from '../lib/queries';
import { useSavedSessions } from '../lib/savedSessions';
import { useSession } from '../lib/session';
import { isTabActive, TABS } from '../lib/tabs';
import { AccountLink } from './AccountLink';

/**
 * What a laptop gets instead of the tab bar: the lockup, the same destinations in a row, and the
 * avatar. A bar pinned to the bottom of a 900px window is far from wherever the eye is, and five
 * icons across a phone's width read as an app that has been stretched rather than one that fits.
 *
 * The profile has no item of its own here, as it has no tile on the home screen: the avatar is the
 * way to it everywhere in this app, and drawing both would advertise one destination twice.
 *
 * It is 5rem tall and sticky, which the two screens with sticky headers of their own answer with
 * `lg:top-20` — otherwise their search box scrolls up under this.
 */
export function DesktopNav({ event }: { event: PublicEvent | undefined }) {
  const { session } = useSession();
  const saved = useSavedSessions();
  const { pathname } = useLocation();

  const logo = assetUrl(event?.branding.logoAssetId);
  const tabs = TABS.filter((tab) => tab.to !== '/account');
  const onAccount = pathname.startsWith('/account');

  return (
    <header className="sticky top-0 z-30 hidden h-20 border-b border-border-2 bg-surface lg:block">
      <div className="px-gutter mx-auto flex h-full max-w-5xl items-center gap-6">
        <Link to="/" aria-label="Home" className="shrink-0">
          <img
            src={logo ?? '/logo-lockup.png'}
            alt={event?.name ?? 'Go For The Greens'}
            className="h-auto max-h-14 w-[104px] object-contain object-left"
            // Stated only for the bundled lockup, as in the phone header: an uploaded logo is
            // whatever shape the organizer sent, and asserting these would squash it.
            width={logo ? undefined : 400}
            height={logo ? undefined : 275}
          />
        </Link>

        <nav aria-label="Main" className="min-w-0 flex-1">
          <ul className="flex items-center gap-1">
            {tabs.map((tab) => {
              const isActive = isTabActive(tab, pathname);

              return (
                <li key={tab.to}>
                  <Link
                    to={tab.to}
                    aria-current={isActive ? 'page' : undefined}
                    className={`text-row rounded-button min-h-tap flex items-center gap-2 px-3 ${
                      isActive ? 'bg-sand-2 font-semibold text-ink' : 'font-medium text-ink-3'
                    }`}
                  >
                    <Icon name={tab.icon} size={19} strokeWidth={isActive ? 2 : 1.5} />
                    {tab.label}
                    {tab.to === '/schedule' && saved.ids.size > 0 && (
                      <span className="text-eyebrow flex size-4 items-center justify-center rounded-full bg-brand font-bold text-white tabular-nums">
                        {saved.ids.size}
                        <span className="sr-only"> saved</span>
                      </span>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {session && <AccountLink session={session} current={onAccount} />}
      </div>
    </header>
  );
}
